/**
 * `Daily Report` — the **Consolidated Daily UM Debrief Report** on the phone.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 74 — THE PER-MATCH RESTRUCTURE, AND THE TWO RULINGS BEHIND IT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Spec 13 §6 supersedes spec 05's per-umpire pack with a report whose spine is **one section per
 * match, in chronological order**. Each section carries the match line, its appointed U1/U2/VU
 * with role codes, the Call 6 organised summary for each of them, the released observations
 * behind each summary under `Show Notes ▾`, and spec 13 §8's `What The Umpire Said`. Each section
 * links down to that match's own Match Debrief screen — the room the responses were written in.
 *
 * **Ruling R-D5 — frozen core, live addenda.** Everything but the response sections is the dated
 * artefact spec 11 §4.3 describes. A response released after the report was built renders under
 * one stamped strip, `Debrief Held After This Report Was Built · Added hh:mm`. The API decides
 * which is which (`applyLiveResponseAddenda`); this page renders the marking and never computes
 * it, and the stored artefact is never rewritten by a read.
 *
 * **Ruling R-D6 — the report no longer builds at a fixed hour.** It builds about thirty minutes
 * after the last match of the day ends, never past a configured backstop, and not at all on a
 * rest day (`apps/api/src/debrief/report-schedule.ts`). Every sentence on this page that used to
 * promise 21:30 was rewritten rather than left standing: a disclosure that has stopped being true
 * is worse than none.
 *
 * **UX finding 22 — scanability.** A jump index of match numbers at the top, and a sticky header
 * on every match section, so a manager three umpires deep still knows which match she is in.
 *
 * **The day-level sections are kept, not replaced.** Spec 07's own amendment says dropping the
 * one-page rule means *"no umpire dropped from the duty list"*, so the duty list stands; the
 * flags are where spec 05's *"who nobody has seen yet"* lives and §6 leaves them unchanged;
 * `Also Raised` is the `general` kind, which spec 05's banner says nothing changes about; and
 * tomorrow's appointments still close the meeting on a decision.
 *
 * Authority: `um-app/13-match-debrief.md` §6 and §8; `um-app/05-debrief-pack.md` §2.1 (the header and the date control §2.1 says the
 * mockup is missing), §2.2 (`Umpires On Duty Today`), §2.3 (`Flags For Discussion`), §2.4
 * (`Tomorrow's Appointments`), §2.5 (the footer) and §4 (the states);
 * `um-app/11-release-and-debrief.md` §4.2 (what the assembly does) and Q-11c (`Also Raised`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE KICKER SAYS WHICH OF THREE THINGS YOU ARE LOOKING AT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §2.1's kicker used to read `Auto-Generated · 21:30`, and until run 74 this page printed
 * `Assembled 21:30` on a stored artefact. **Ruling R-D6 makes that a hard-coded claim about an
 * hour the report may not have been built at**, so the kicker now prints the artefact's own
 * stamp:
 *
 *   `Assembled 21:10`   — the stored artefact, at the time it was actually built. The figure is
 *                         `assembledAt`, in Tokyo, and it is a fact rather than a schedule.
 *   `Regenerated 22:14` — the same artefact after a Technical Delegate rebuilt it following a
 *                         late release (spec 11 §4.3, spec 07 §5's `Regenerated hh:mm`).
 *   `Computed On Open`  — **the honest fallback**, kept whole from run 55: no report was stored
 *                         for this day, so it was worked out on this read. That is the state
 *                         before the tournament's first build, on a day the job was not running
 *                         for, and on any day whose assembly failed.
 *
 * The stamp is rendered in **Tokyo time**, because the tournament runs on Tokyo time (spec 11
 * §4) and a stamp shown in the reader's own zone would not line up with the day it sits beside.
 * A stored artefact whose stamp cannot be read falls back to the bare word `Assembled` rather
 * than printing `Assembled Invalid Date` — the report really was assembled, and the stamp is the
 * part that is unreadable. Everything else in §5's copy inventory is kept verbatim:
 * `Flags For Discussion`, `Tomorrow's Appointments`, `Nothing Leaves Without A Human Pressing
 * A Button`.
 *
 * `Umpires On Duty Today` becomes `Umpires On Duty` for the same reason in miniature: the
 * matchday selector means the pack on screen is often not today's, and a header saying
 * `Today` above a previous day's evidence would be wrong in the ordinary case.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * §2.2'S RIGHT-HAND KICKER IS EARNED, NOT PRINTED (RUN 59)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Run 55 drew `Panel Summary` there and said why: spec 12's Call 3 was unbuilt, the line on each
 * row was a deterministic sentence the API computed from the released notes, and *"labelling
 * that `AI One-Liners` would credit a model that never ran"*. **Run 59 built Call 3**, so the
 * kicker is now §2.2's own — `AI One-Liners` — **and only when `ai.state` is `present`**. In
 * every other state it reads `Panel Summary` again, because in every other state that is what
 * the rows carry. That is the supersession of the copy decision taken here on 2026-09-08.
 *
 * Each row now draws up to two lines. The AI sentence when there is one, and **always** the
 * deterministic summary beneath it — the count the sentence is made of. The second is not a
 * fallback that vanishes when the first arrives; a panel reading a summary aloud is entitled to
 * see how much evidence stands behind it.
 *
 * When any AI text is missing, the API's own one-sentence notice is printed above the sections
 * it is about (`ai.notice`, written in `debrief/ai.ts`). That notice is how spec 12 §4's *"the
 * TD is notified"* is kept: the Technical Delegate reads this pack like everybody else, and
 * ruling R5 caps the system's scheduled mail at one message a night, so a second email would
 * have broken a disclosure rather than kept a clause.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ABSENT RATHER THAN STUBBED, AND WHY EACH ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  * **The `Discussed` control** (§2.3). It writes a mark per flag per day, and there is no
 *    write path for one — no route, no table, no store. A toggle that forgot its state on
 *    every read would be worse than no toggle: a panel that ticked four flags and came back
 *    to four unticked ones would stop trusting the screen.
 *  * **The viewed-event and the unread badge on Today's debrief pill** (§3, spec 01 §2.7).
 *    Both need the `viewed` write the same missing path would carry, and `UmShell` records
 *    that the pill itself is *"absent rather than stubbed"*.
 *  * **The `Regenerate` control** (spec 11 §4.3, spec 07 §5). The ENDPOINT is built and is
 *    TD-only (`routes/td-debrief.ts`); the control is not, because this is the managers' screen
 *    and §4.3 makes the press the Technical Delegate's. There is no TD debrief surface in this
 *    build for it to sit on, and a button placed here would be one the reader is refused.
 *  * **The Today-screen pill badge** (§4.2 step 6, spec 01 §2.7). It needs the `viewed` write
 *    the missing path above would carry, and a badge that never cleared would be worse than
 *    none. `UmShell` records the pill itself as absent rather than stubbed.
 *  * ~~**The A4 document** (spec 07) and **any export**. No download, no print view, no PDF.~~
 *    **Superseded 2026-09-09.** Spec 07's document is built, at `/um/debrief/print`, and the
 *    `Print / A4` link in this header is the way to it. It is a print-styled page over the
 *    browser's own dialogue — the hearings path — and NOT §3's server-rendered stored PDF;
 *    `UmDebriefDocumentPage`'s header states that and lists what spec 07 asks for that the pack
 *    cannot honestly supply. There is still no download and no file this system writes.
 *  * ~~**§2.3's alignment (`⇄`) and positive-trend (`△`) flags.**~~ **Built in run 59** — both
 *    deterministically, in code, from released notes and the schedule alone. `debrief/flags.ts`
 *    records the one place the spec's wording could not be taken literally (§2.3's *"read in
 *    opposite directions"*, which nothing in this system stores) and what is flagged instead.
 *  * **§4's `Not generated yet`, `Generating`, `Generation failed` and `Offline` states.**
 *    They describe a scheduled artefact and a cache this build does not have. What IS drawn
 *    is §4's states that apply to an on-demand pack: a loading skeleton, `No Matches` on a
 *    day with no fixture, and honest empties in every section.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FOOTER NOW CARRIES §2.5'S RULING-R5 FOOTNOTE, BECAUSE IT IS TRUE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Run 55 deliberately did NOT print *"One Message Goes Out On A Schedule"*, because it
 * disclosed an email that build did not send, and a disclosure of something that does not
 * happen is not honesty — it is noise. **Run 58 sends it** (ruling R5: auto-sends, one per
 * night, link only, no note text, no attachment), so the footnote is printed, in the plainest
 * form of what actually goes out, and the manager's own opt-out sits directly beneath it —
 * spec 09 §3.4 puts the recap in a Settings sheet that is not built, and the next best place
 * for a switch is beside the sentence that discloses what it switches off.
 *
 * The switch fails SOFT: if the setting cannot be read, the control is not drawn at all and
 * the disclosure stays. A toggle rendered in a guessed position is a toggle that tells a
 * manager their copy is on when nobody knows whether it is.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE A ROW GOES WHEN IT IS TAPPED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §2.2: *"Rows are tappable and route to that umpire's timeline (spec 04) filtered to today."*
 * **Spec 04's timeline is not built.** The closest surface that exists is the Report Studio's
 * evidence pane at `/um/umpires/:tmsPersonId/report` — the same released notes, grouped by
 * criterion instead of by day, and the pane `UmUmpiresPage` already routes to. Rows therefore
 * link there, and only for an umpire the reader OWNS: the route is `requireUm` plus an
 * ownership check, so a link to somebody else's umpire would be a link to a 403. An unowned
 * umpire's row is drawn in full and simply is not a link, which is the honest version of a
 * destination that does not exist for that reader.
 */

import { Link } from "react-router-dom";
import { useState } from "react";

import {
  fetchDebriefEmailSetting,
  fetchDebriefPack,
  fetchMyUmpires,
  setDebriefEmailSetting,
} from "../../api/client.js";
import type {
  DebriefFlag,
  DebriefGeneralNote,
  DebriefMatchResponse,
  DebriefMatchSection,
  DebriefMatchUmpire,
  DebriefPackBody,
  DebriefTomorrowFixture,
  DebriefUmpireRow,
} from "../../api/types.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import {
  addendumStripLine,
  matchDebriefPath,
  matchSectionAnchor,
  matchdayLabel,
  RESPONSES_HEADING,
  splitSummary,
  summaryStamp,
  tokyoClockTime,
  tokyoDateKicker,
} from "./matchDebrief.js";

/**
 * Where spec 07's A4 document lives, for a named matchday or for the server's own pick.
 *
 * It is defined HERE rather than beside the document it points at, and only because the
 * dependency has to run one way: `UmDebriefDocumentPage` reads {@link debriefKicker} off this
 * module so the paper and the phone cannot word the pack's provenance differently, and a path
 * helper imported back the other way would close that into a cycle.
 */
export function debriefDocumentPath(day: string | null): string {
  const base = `${UM_ROOT_PATH}/debrief/print`;
  return day === null ? base : `${base}?day=${encodeURIComponent(day)}`;
}

/** §2.1's pill. The count of the day's fixtures and nothing else. */
export function matchesPill(matchCount: number): string {
  return matchCount === 1 ? "1 Match" : `${matchCount} Matches`;
}

/**
 * `Matchday 4 · 21 September` — §2.1's selector label.
 *
 * The matchday NUMBER is the day's position in the tournament's own list of days with
 * fixtures, computed from that list rather than stored: a rest day is not a matchday, and
 * counting dates would make it one.
 *
 * **Moved to `matchDebrief.ts` in run 75** and re-exported here unchanged: the Technical
 * Delegate's read-only Match Debrief screen prints the same label in its own matchday selector.
 */
export { matchdayLabel };

/**
 * §2.1's kicker, from the pack's own provenance. Three states and no fourth — see the header.
 *
 * Both stamps are rendered in **Tokyo time**, because the tournament runs on Tokyo time (spec 11
 * §4): a stamp in the reader's own zone would not line up with the day it sits beside. An
 * instant the browser cannot parse falls back to the bare word rather than printing
 * `Assembled Invalid Date` — the report really was assembled, and the stamp is the part that is
 * unreadable.
 *
 * **Amended run 74 (ruling R-D6).** It used to print the literal `Assembled 21:30`. The report
 * no longer builds at a fixed hour, so the kicker prints the artefact's OWN build stamp.
 */
export function debriefKicker(pack: {
  source: string;
  assembledAt: string | null;
  regeneratedAt: string | null;
}): string {
  if (pack.source !== "stored") return "Computed On Open";
  if (pack.regeneratedAt !== null) {
    const stamp = tokyoClockTime(pack.regeneratedAt);
    if (stamp !== null) return `Regenerated ${stamp}`;
  }
  const built = pack.assembledAt === null ? null : tokyoClockTime(pack.assembledAt);
  return built === null ? "Assembled" : `Assembled ${built}`;
}

/**
 * `2026-09-22T13:14:00Z` → `22:14`. Tokyo, 24-hour, and `null` for anything unparseable.
 *
 * **Moved to `matchDebrief.ts` in run 75** — the Technical Delegate's read-only Match Debrief
 * screen stamps its summaries with the same clock and cannot import a UM page to get it — and
 * re-exported here unchanged, so nothing that already named it has to move.
 */
export { tokyoClockTime };

function competitionClass(competitionId: string): string {
  return competitionId === "1916" ? "men" : "women";
}

/** §2.2's row. Tappable only when the reader owns that umpire — see the header. */
function DutyRow({
  row,
  owned,
}: {
  row: DebriefUmpireRow;
  owned: boolean;
}): React.JSX.Element {
  const who = (
    <>
      <span className="dbrow__avatar" aria-hidden="true">
        {row.initials}
      </span>
      <span className="dbrow__nm">
        {row.mediaName}
        <span className="dbrow__sub num">
          {row.country}
          {row.slots.length === 0 ? "" : ` · ${row.slots.join(" · ")}`}
        </span>
      </span>
    </>
  );

  return (
    <li className="dbrow" data-testid={`duty-${row.tmsPersonId}`}>
      {owned ? (
        <Link
          className="dbrow__who"
          to={`${UM_ROOT_PATH}/umpires/${row.tmsPersonId}/report`}
          title="Open this umpire's evidence"
        >
          {who}
        </Link>
      ) : (
        <span className="dbrow__who">{who}</span>
      )}
      {/*
       * §2.2's line, in the two layers the pack actually has.
       *
       * `aiLine` is spec 12 §4's descriptive sentence and is drawn only when the API sent one.
       * Beneath it — ALWAYS, whether or not there is an AI line — sits the deterministic
       * summary the API composes from the released notes themselves. The second is not a
       * fallback that disappears when the first arrives: it is the count the sentence is made
       * of, and a panel reading a summary is entitled to see how much evidence stands behind
       * it. Neither is ever composed here.
       */}
      {row.aiLine === null ? null : (
        <span className="dbrow__ai" data-testid={`ai-${row.tmsPersonId}`}>
          {row.aiLine}
        </span>
      )}
      <span className={row.aiLine === null ? "dbrow__ai" : "dbrow__sum num"}>
        {row.summary}
      </span>
    </li>
  );
}

/**
 * §2.3's `.flagcard`, in all three of its kinds.
 *
 * **The glyph is tinted by kind and is never the only channel** (gap A4): the title says what
 * the flag is in full, and a reader who sees no colour at all loses nothing.
 *
 * **Which words are shown.** `aiDetail` when the API sent one — spec 12 §4's phrasing of the
 * same computed facts — and the API's own deterministic `detail` when it did not. The two are
 * never both drawn: unlike a duty row, where the summary is the arithmetic behind the sentence,
 * a flag's two paragraphs say the same thing twice. Whichever is drawn, the flag itself was
 * computed in code (`debrief/flags.ts`) and no model decided it applied.
 */
function FlagCard({ flag }: { flag: DebriefFlag }): React.JSX.Element {
  return (
    <li className={`flagcard flagcard--${flag.kind}`} data-testid={`flag-${flag.id}`}>
      <span className="flagcard__glyph" aria-hidden="true">
        {flag.glyph}
      </span>
      <span className="flagcard__body">
        <b className="flagcard__title">{flag.title}</b>
        <span className="flagcard__detail">{flag.aiDetail ?? flag.detail}</span>
      </span>
    </li>
  );
}

/* ── Run 74 — spec 13 §6's per-match sections ──────────────────────────────── */

/**
 * **UX finding 22, the first half: a jump index.**
 *
 * A per-match report is long — six matches, three umpires each, the notes behind every summary —
 * and the manager who wants M09 should not scroll past M07 and M08 to reach it. The index is
 * `M07 · M08 · …` at the top, and each entry is a plain in-page anchor: `href="#…"` rather than a
 * router `Link`, because the target is a heading on the page that is already open and a router
 * navigation would remount the report to arrive at the same place.
 *
 * It is drawn only when there are two or more matches. An index of one entry is furniture.
 */
function MatchJumpIndex({
  matches,
}: {
  matches: readonly DebriefMatchSection[];
}): React.JSX.Element | null {
  if (matches.length < 2) return null;
  return (
    <nav className="rpjump" aria-label="Jump to a match" data-testid="report-jump">
      <span className="rpjump__lbl">Jump To</span>
      {matches.map((section) => (
        <a
          className={`rpjump__go rpjump__go--${competitionClass(section.competitionId)}`}
          key={section.tmsMatchDbId}
          href={`#${matchSectionAnchor(section.displayMatchNo)}`}
          data-testid={`report-jump-${section.displayMatchNo}`}
        >
          {section.displayMatchNo}
        </a>
      ))}
    </nav>
  );
}

/**
 * One umpire inside a match section: the seat, the organised summary, and its evidence.
 *
 * **The summary is Call 6's and is never recomposed here.** The alignment sentence is split off
 * with `splitSummary` — the same function the Match Debrief screen uses, for the same reason its
 * note gives — and, unlike that screen, BOTH halves are drawn: this report is the panel's own
 * meeting agenda and the Umpires Managers are the only readers, so there is no Room View to
 * withhold `For The Managers` from. The kicker is kept, because it says what the paragraph is.
 */
function MatchUmpireBlock({ umpire }: { umpire: DebriefMatchUmpire }): React.JSX.Element {
  const split =
    umpire.summary === null ? null : splitSummary(umpire.summary, umpire.hasAlignment);
  const stamp = summaryStamp(
    umpire.generatedAt === null ? null : tokyoClockTime(umpire.generatedAt),
    umpire.noteCount,
  );

  return (
    <li className="rpump" data-testid={`report-umpire-${umpire.tmsPersonId}`}>
      <div className="mduhead">
        <span className="dbrow__avatar" aria-hidden="true">
          {umpire.initials}
        </span>
        <span className="dbrow__nm">
          {umpire.mediaName}
          {/* The role code is on EVERY block, beside the name it belongs to: which seat a
              person was in is the first thing the meeting needs. */}
          <span className="dbrow__sub num">
            {`${umpire.country} · ${umpire.slot} · ${umpire.slotLabel}`}
          </span>
        </span>
      </div>

      {split === null ? (
        <p className="mdai__notice" data-testid={`report-notice-${umpire.tmsPersonId}`}>
          {umpire.notice ?? "No summary was produced for this umpire."}
        </p>
      ) : (
        <div className="mdai" data-testid={`report-summary-${umpire.tmsPersonId}`}>
          {/* The house kicker for an organised summary — the same one the Match Debrief screen
              prints, so a manager meets the same words in the room and in the evening. */}
          <span className="card__kicker">AI Summary</span>
          {split.body.map((paragraph, index) => (
            <p className="mdai__p" key={index}>
              {paragraph}
            </p>
          ))}
          {split.alignment === null ? null : (
            <>
              <span className="card__kicker">For The Managers</span>
              <p className="mdai__p" data-testid={`report-align-${umpire.tmsPersonId}`}>
                {split.alignment}
              </p>
            </>
          )}
          {stamp === null ? null : <span className="mdai__stamp num">{stamp}</span>}
        </div>
      )}

      {/*
       * THE RELEASED OBSERVATIONS, behind a native `<details>` — **closed by default, and forced
       * open in print**. The idiom is `UmMatchDebriefPage`'s, verbatim and for its reasons:
       * `<details>`/`<summary>` is keyboard-operable and screen-reader-announced with no code,
       * and the browser's own find-in-page opens it. The print rule is in `editorial.css`, so a
       * sheet handed round a room carries its evidence rather than a row of closed triangles.
       */}
      <details className="mddisc" data-testid={`report-notes-${umpire.tmsPersonId}`}>
        <summary className="mddisc__sum">
          <span className="card__kicker">Show Notes ▾</span>
          <span className="card__note num">
            {umpire.observations.length === 1
              ? "1 Note"
              : `${String(umpire.observations.length)} Notes`}
          </span>
        </summary>
        <div className="mddisc__body">
          {umpire.observations.length === 0 ? (
            <p className="card__body">Nothing has been released about this umpire yet.</p>
          ) : (
            <ul className="mdnotes">
              {umpire.observations.map((note) => (
                <li className="mdnote" key={note.id}>
                  <span className="mdnote__crit">{note.criterion}</span>
                  <span className="mdnote__when num">{note.when ?? "Unstamped"}</span>
                  <span className="mdnote__au num">
                    {/* §3's attribution: the short display name, never a country code. */}
                    UM · {note.authorShortName ?? "Unattributed"}
                  </span>
                  <span className="mdnote__txt">{note.bodyEn}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </li>
  );
}

/**
 * Spec 13 §8's `What The Umpire Said`, for one match — **and ruling R-D5's live half**.
 *
 * The responses released before the report was built are the frozen core. Any released
 * afterwards arrive under one stamped strip, once, above them all: a strip per response would
 * put the same sentence on the page four times on the night a debrief ran long.
 */
function WhatTheUmpireSaid({
  responses,
}: {
  responses: readonly DebriefMatchResponse[];
}): React.JSX.Element {
  const core = responses.filter((response) => !response.addedAfterBuild);
  const addenda = responses.filter((response) => response.addedAfterBuild);

  const row = (response: DebriefMatchResponse): React.JSX.Element => (
    <li className="mdnote" key={response.id}>
      <span className="mdnote__au num">
        {`${response.mediaName}${response.slot === null ? "" : ` · ${response.slot}`}`}
        {` · Recorded By UM · ${response.authorShortName ?? "Unattributed"}`}
      </span>
      <span className="mdnote__txt">{response.bodyEn}</span>
    </li>
  );

  return (
    <div className="mdsaid" data-testid="report-said">
      <div className="notelist__head">
        <span className="card__kicker">{RESPONSES_HEADING}</span>
        <span className="card__note num">{responses.length}</span>
      </div>
      {responses.length === 0 ? (
        <p className="card__body">
          Nothing has been written down from this match&rsquo;s debrief yet.
        </p>
      ) : (
        <>
          {core.length === 0 ? null : <ul className="mdnotes">{core.map(row)}</ul>}
          {addenda.length === 0 ? null : (
            <>
              {/* RULING R-D5. The report's core is the dated artefact; this section is not. */}
              <p className="rpadd" data-testid="report-addendum-strip">
                {addendumStripLine(addenda[0]?.addedAt ?? null)}
              </p>
              <ul className="mdnotes">{addenda.map(row)}</ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * **One match, as spec 13 §6 restructures the evening around it.**
 *
 * The header is sticky (UX finding 22's second half) so a manager reading the third umpire's
 * notes still knows which match she is in, and it is a LINK down to that match's own Match
 * Debrief screen — the room where the responses beneath it were written.
 */
function ReportMatchSection({
  section,
}: {
  section: DebriefMatchSection;
}): React.JSX.Element {
  return (
    <section
      className="card rpmatch"
      id={matchSectionAnchor(section.displayMatchNo)}
      data-testid={`report-match-${section.displayMatchNo}`}
    >
      <div className="rpmatch__head">
        {/* Colour is never the only channel (gap A4): the M/W letter is in the id itself. */}
        <span className={`fxline__id fxline__id--${competitionClass(section.competitionId)}`}>
          {section.displayMatchNo}
        </span>
        <span className="rpmatch__who">
          {section.teams ?? "Teams Not Yet Known"}
          {section.score === null ? null : (
            <span className="rpmatch__score num">{section.score}</span>
          )}
        </span>
        <span className="rpmatch__when num">
          {[section.pitch, section.time].filter((part) => part !== null).join(" · ")}
        </span>
        <Link
          className="rpmatch__go"
          to={matchDebriefPath(section.tmsMatchDbId, section.competitionId)}
          data-testid={`report-open-${section.displayMatchNo}`}
        >
          Match Debrief ›
        </Link>
      </div>

      {section.umpires.length === 0 ? (
        <p className="card__body" data-testid={`report-no-umpires-${section.displayMatchNo}`}>
          No umpire appointment for this match is held in this system, so there is nobody to
          summarise. Anything released about it is still on the umpires&rsquo; own timelines.
        </p>
      ) : (
        <ul className="rpumps">
          {section.umpires.map((umpire) => (
            <MatchUmpireBlock key={umpire.tmsPersonId} umpire={umpire} />
          ))}
        </ul>
      )}

      <WhatTheUmpireSaid responses={section.responses} />
    </section>
  );
}

function AlsoRaisedRow({ note }: { note: DebriefGeneralNote }): React.JSX.Element {
  return (
    <li className="dbgen" data-testid={`also-${note.id}`}>
      <span className="dbgen__who num">
        {/* §3's attribution: the author's short display name, never a country code. */}
        UM · {note.authorShortName ?? "Unattributed"}
        {note.matchNo === null ? "" : ` · ${note.matchNo}`}
      </span>
      <span className="dbgen__body">{note.bodyEn}</span>
    </li>
  );
}

function TomorrowRow({ fixture }: { fixture: DebriefTomorrowFixture }): React.JSX.Element {
  return (
    <li className="apptline" data-testid={`tomorrow-${String(fixture.tmsMatchDbId)}`}>
      {/* Colour is never the only channel (gap A4): the M/W letter is in the text. */}
      <span
        className={`apptline__id apptline__id--${competitionClass(fixture.competitionId)}`}
      >
        {fixture.displayMatchNo}
      </span>
      <span className="apptline__what">
        {fixture.onField.length === 0
          ? "Not Appointed Yet"
          : fixture.onField.map((entry) => entry.mediaName).join(" · ")}
      </span>
      <span className="apptline__when num">{fixture.time}</span>
    </li>
  );
}

/**
 * Ruling R5's one permitted opt-out — *"a UM may turn their own copy off in Settings"*.
 *
 * It lives here rather than on a Settings screen because there is no Settings screen: spec 09
 * §3.4's sheet is recorded by `UmShell` as absent rather than stubbed, and standing one up to
 * hold a single switch would have shipped four empty sections and a privacy recap nobody had
 * reviewed. Beside the sentence that discloses the message is the next best place for the
 * switch that turns it off.
 *
 * **It fails soft.** While the setting is being read, and if it cannot be read at all, nothing
 * is drawn — a toggle rendered in a guessed position would tell a manager their copy is on when
 * nobody knows whether it is. The disclosure above it stays either way, because the message
 * goes out whether or not this control can be shown.
 *
 * The press sends the new value and renders **what the server answers with**, not what the tap
 * assumed. A switch that flipped optimistically and failed would leave a manager believing they
 * had opted out of a message that was still coming.
 */
function DebriefEmailSwitch(): React.JSX.Element | null {
  const setting = useAsync(() => fetchDebriefEmailSetting(), []);
  const [override, setOverride] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (setting.status !== "ready") return null;
  const enabled = override ?? setting.data.enabled;

  async function toggle(): Promise<void> {
    setBusy(true);
    setFailed(false);
    try {
      const answer = await setDebriefEmailSetting(!enabled);
      setOverride(answer.enabled);
    } catch {
      // Nothing was changed and the control says so. The pack, and the message, are unaffected.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dbmail" data-testid="debrief-email-switch">
      <span className="dbmail__state">
        {enabled
          ? "Your copy of the nightly report email is on."
          : "Your copy of the nightly report email is off."}
      </span>
      <button
        type="button"
        className="dbmail__btn"
        disabled={busy}
        onClick={() => {
          void toggle();
        }}
      >
        {enabled ? "Turn My Copy Off" : "Turn My Copy On"}
      </button>
      {failed ? (
        <span className="dbmail__fail" role="alert">
          That did not save. Nothing has changed — try again.
        </span>
      ) : null}
    </div>
  );
}

export function UmDebriefPage(): React.JSX.Element {
  /**
   * `null` means "whichever day the server picks" — the most recent matchday at or before
   * today. The selector sets a day explicitly from that moment on, so a UM who has walked
   * back to Matchday 2 is not pulled forward again by a re-render.
   */
  const [day, setDay] = useState<string | null>(null);
  const pack = useAsync(() => fetchDebriefPack(day ?? undefined), [day]);

  /**
   * The reader's own umpires, read ONLY to decide which duty rows are links.
   *
   * It is a separate, already-existing call rather than a field on the pack, because
   * ownership is a fact about the reader and the pack is a fact about the matchday — putting
   * the reader into the pack's body would make it a per-caller response, which is exactly
   * what the released surfaces are built not to be. A failure here costs the links and
   * nothing else: the pack still renders in full.
   */
  const mine = useAsync(() => fetchMyUmpires(), []);
  const ownedIds = new Set(
    mine.status === "ready" ? mine.data.umpires.map((umpire) => umpire.tmsPersonId) : [],
  );

  const body: DebriefPackBody | null = pack.status === "ready" ? pack.data : null;

  return (
    <>
      <header className="umsect">
        {/* One of `Assembled hh:mm`, `Regenerated hh:mm` and `Computed On Open` — the API
            says which, and the page never guesses. See the header. */}
        <span className="umsect__kicker" data-testid="debrief-kicker">
          {body === null
            ? "Daily Debrief"
            : debriefKicker(body)}
        </span>
        <h2>
          Daily UM Debrief Report
          {body === null ? null : (
            <span className="pill pill--acc num" data-testid="matches-pill">
              {matchesPill(body.matchCount)}
            </span>
          )}
        </h2>

        {/* §2.1's date control, which the mockup is missing: the pack is per matchday and
            previous days must be reachable. A native select rather than a bespoke menu — it
            is a list of dates on a phone, and the platform's own picker is the accessible
            one. */}
        {body !== null && body.days.length > 0 ? (
          <label className="dbday" data-howto="matchday">
            <span className="dbday__lbl">Matchday</span>
            <select
              className="dbday__sel"
              value={body.day ?? ""}
              onChange={(event) => setDay(event.target.value)}
              data-testid="matchday-select"
            >
              {body.days.map((entry) => (
                <option key={entry} value={entry}>
                  {matchdayLabel(body.days, entry)}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {/*
         * Spec 07's document, one quiet link from the pack it is made of.
         *
         * It is drawn only once a pack is on screen, and it carries THE DAY THAT IS ON SCREEN
         * — a `Print / A4` that silently printed a different matchday from the one the manager
         * was reading would be the worst kind of wrong. On the pre-tournament fallback, where
         * the pack has no day, it passes none and the document asks the API for the same
         * default this screen did.
         */}
        {body === null ? null : (
          <Link
            className="dbprint"
            to={debriefDocumentPath(body.day)}
            data-testid="debrief-print-link"
          >
            Print / A4
          </Link>
        )}

        <HowToUse id="um-debrief" title="How to use the Daily Report">
          <li>
            This is the panel meeting&rsquo;s agenda, assembled from the notes the team has
            released. It is put together about half an hour after the last match of the day
            ends; on a day that has no report yet it is worked out when you open the tab, and
            the line above the title says which. On a day with no matches, no report is built.
          </li>
          <li>
            <HowToLink to="matchday">The matchday picker</HowToLink> reaches every previous
            day of the tournament.
          </li>
          <li>
            Only <b>released</b> notes are in it. Your drafts and your locked notes are not,
            and neither is anybody else&rsquo;s.
          </li>
          <li>
            Tapping an umpire you report on opens their evidence. Nothing on this screen is
            sent anywhere.
          </li>
          <li>
            Each match is a section of its own, in the order the day was played. <b>Jump To</b>
            at the top takes you straight to a match, and <b>Match Debrief ›</b> on a match
            opens the screen its responses were written on.
          </li>
          <li>
            <b>What The Umpire Said</b> stays live. If a debrief is held after the report was
            built, what was said appears there under a line saying when it was added — the rest
            of the report is the dated document and does not change.
          </li>
          <li>
            <b>Print / A4</b> lays this same report out for the room, over as many pages as it
            needs. Your browser prints it or saves it on your device; nothing is uploaded and
            nothing is sent.
          </li>
        </HowToUse>
      </header>

      {/* §4's `Generating` becomes a plain loading skeleton: the pack is computed on this
          read, so the honest thing to say is that it is loading, not that it is being built
          to a schedule. Never a blank tab. */}
      {pack.status === "loading" ? <Skeleton rows={6} /> : null}
      {pack.status === "error" ? <FailBanner error={pack.error} /> : null}

      {body === null ? null : (
        <>
          {body.dayIsFuture ? (
            <section className="card">
              <div className="card__head">
                <span className="card__kicker">This Day Has Not Been Played</span>
                <span className="card__note num">{body.day}</span>
              </div>
              <p className="card__body">
                The tournament has not opened yet, so this is the first matchday rather than a
                debrief. It fills as notes are released.
              </p>
            </section>
          ) : null}

          {/* §4's `No Matches Today` (rest day). The flags and tomorrow's appointments
              sections stay below it, because both remain useful on a rest day. */}
          {body.matchCount === 0 ? (
            <section className="card">
              <p className="card__body" data-testid="no-matches">
                No Matches On This Day.
              </p>
            </section>
          ) : null}

          {/*
           * §4's `Generation failed`, in the form this build's pack actually takes: the pack is
           * never withheld, so what the screen shows is the pack plus one honest sentence saying
           * that its AI summaries are missing and that the released notes below are whole. The
           * API writes the sentence (`debrief/ai.ts`) and this renders it verbatim, so the phone
           * and any future document cannot word the same failure differently.
           *
           * It sits above the sections it is about — the duty rows and the flags — and is absent
           * altogether when there is nothing to explain.
           */}
          {body.ai.notice === null ? null : (
            <p className="dbainote" data-testid="ai-notice">
              {body.ai.notice}
            </p>
          )}

          {/*
           * SPEC 13 §6'S SPINE — one section per match, in chronological order, before
           * everything the day says about itself.
           *
           * The day-level sections BELOW it are kept rather than replaced. Spec 07's own
           * amendment is explicit that dropping the one-page rule means *"no umpire dropped from
           * the duty list"*, so the duty list survives the restructure; the flags are where
           * *"who nobody has seen yet"* lives (spec 13 §6 keeps them unchanged); `Also Raised` is
           * the `general` kind, which spec 05's banner says nothing about changes; and
           * tomorrow's appointments still close the meeting on a decision.
           */}
          <MatchJumpIndex matches={body.matches} />
          {body.matches.map((section) => (
            <ReportMatchSection key={section.tmsMatchDbId} section={section} />
          ))}

          <section className="card" data-testid="duty-card">
            <div className="card__head">
              <span className="card__kicker">Umpires On Duty</span>
              {/* §2.2's own right-hand kicker, but ONLY when a model actually wrote the lines
                  under these names. `Panel Summary` in every other state — a heading crediting
                  an AI over a deterministic count would be a claim nobody made. */}
              <span className="card__note" data-testid="duty-kicker">
                {body.ai.state === "present" ? "AI One-Liners" : "Panel Summary"}
              </span>
            </div>
            {body.umpiresOnDuty.rows.length === 0 ? (
              <p className="card__body">
                No umpire has been appointed to this day&rsquo;s matches and no note about one
                has been released. There is nothing to discuss yet.
              </p>
            ) : (
              <>
                {body.umpiresOnDuty.source === "released_notes" ? (
                  <p className="card__body" data-testid="duty-fallback">
                    The Umpires Managers&rsquo; appointments are not held in this system for
                    this day, so this list is the umpires the day&rsquo;s released notes are
                    about — not an appointment sheet.
                  </p>
                ) : null}
                <ul className="dbrows">
                  {body.umpiresOnDuty.rows.map((row) => (
                    <DutyRow
                      key={row.tmsPersonId}
                      row={row}
                      owned={ownedIds.has(row.tmsPersonId)}
                    />
                  ))}
                </ul>
              </>
            )}
          </section>

          <section className="card" data-testid="flags-card">
            <div className="card__head">
              <span className="card__kicker">Flags For Discussion</span>
              <span className="card__note num">
                {body.flags.length === 1 ? "1 Item" : `${body.flags.length} Items`}
              </span>
            </div>
            {body.flags.length === 0 ? (
              <p className="card__body" data-testid="nothing-flagged">
                Nothing Flagged. Nobody is uncovered, no criterion was read twice in the same
                match, and nothing has been rising across the panel.
              </p>
            ) : (
              <ul className="flagcards">
                {body.flags.map((flag) => (
                  <FlagCard key={flag.id} flag={flag} />
                ))}
              </ul>
            )}
          </section>

          <section className="card" data-testid="also-card">
            <div className="card__head">
              <span className="card__kicker">Also Raised</span>
              <span className="card__note num">
                {body.alsoRaised.length === 1 ? "1 Note" : `${body.alsoRaised.length} Notes`}
              </span>
            </div>
            {body.alsoRaised.length === 0 ? (
              <p className="card__body">
                Nothing was raised about the day itself — every released note is about an
                umpire.
              </p>
            ) : (
              <ul className="dbgens">
                {body.alsoRaised.map((note) => (
                  <AlsoRaisedRow key={note.id} note={note} />
                ))}
              </ul>
            )}
          </section>

          <section className="card" data-testid="tomorrow-card">
            <div className="card__head">
              <span className="card__kicker">Tomorrow&rsquo;s Appointments</span>
              <span className="card__note num">
                {body.tomorrow.day === null ? "None" : tokyoDateKicker(body.tomorrow.day)}
              </span>
            </div>
            {body.tomorrow.fixtures.length === 0 ? (
              <p className="card__body">
                {body.tomorrow.day === null
                  ? "This is the last day of the tournament. There is nothing after it to confirm."
                  : "There are no fixtures on the next match day."}
              </p>
            ) : (
              <ul className="apptlines">
                {body.tomorrow.fixtures.map((fixture) => (
                  <TomorrowRow key={fixture.tmsMatchDbId} fixture={fixture} />
                ))}
              </ul>
            )}
          </section>

          {/*
           * §2.5's headline, with ruling R5's footnote now printed beneath it because the
           * message it discloses is now actually sent. The wording is the plainest true
           * account of what goes out: a link, once a night, and nothing from the pack itself.
           * See the page header on why run 55 withheld this and run 58 prints it.
           *
           * The closing clause used to read *"Nothing else leaves — no export, no document."*
           * **Run 66 built the document**, so that sentence would now be false, and a
           * disclosure that has stopped being true is worse than no disclosure — it is the
           * sentence a UM would have trusted. What replaced it says the same true things in
           * the same register: nothing is exported, nothing else leaves on its own, and the A4
           * page is laid out here and printed by the manager on the manager's own device.
           */}
          <p className="dbfoot" data-testid="debrief-foot">
            Nothing Leaves Without A <b>Human Pressing A Button</b>
            <span className="dbfoot__sub">
              One Message Goes Out On A Schedule: once a night, about half an hour after the
              last match of the day ends, the system emails everyone a link to this screen. It
              carries no note text and no attachment, and it is the only message the system sends
              on its own. On a day with no matches nothing is built and nothing is sent. Nothing
              else leaves on its own, and
              nothing is exported. <b>Print / A4</b> lays this pack out as one page and hands it
              to your own browser to print — it stays on your device until you take it
              somewhere.
            </span>
          </p>

          {/* Ruling R5's opt-out, beside the sentence that discloses what it switches off.
              Absent while the setting is unknown — see `DebriefEmailSwitch`. */}
          <DebriefEmailSwitch />
        </>
      )}
    </>
  );
}
